//changed due to package upgrade
import React, { useEffect, useState, useRef } from 'react';
import Modal from '@mui/material/Modal';
import IntlMessages from '@projectK/helpers/IntlMessages';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import AppTextField from '@projectK/components/AppTextField';
import AppGridContainer from '@projectK/components/AppGridContainer';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import jwtAxios from '@projectK/services/auth/JWT';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import {
  alpha,
  Box,
  Button,
  Grid,
  IconButton,
  Autocomplete,
  FormHelperText,
} from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import AppModalConfirmation from '@projectK/components/AppModalConfirmation';
import {isParent } from '@projectK/context/RolesProvider';
import PropTypes from 'prop-types';
const customScrollbarStyle = {
  // Define the styles for the scrollbar
  '&::-webkit-scrollbar': {
    width: '8px',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#44444461', // Lighter black color
    borderRadius: '8px', // Curved border
  },

  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#666', // Lighter black color on hover
  },
};

const getUniqueMembers = (members) => {
  if (!members) return [];
  const seenIds = new Set();
  return members.filter((member) => {
    if (seenIds.has(member.id)) return false;
    seenIds.add(member.id);
    return true;
  });
};

const AddCredits = ({
  isOpen,
  onClose,
  fetchData,
  isAdjustCreditsModalOpen,
  settings,
  fetchUserProduct,
}) => {
  const { user } = useAuthUser();
  const validationSchema = yup.object({
    user_id:
      (isParent(user) && isAdjustCreditsModalOpen) ||
      !isAdjustCreditsModalOpen
        ? yup.array().min(1, () => <IntlMessages id='validation.chooseMember' />)
        : yup.array(),
    product_id: yup.string().required(<IntlMessages id='validation.productRequired' />),
    description: yup.string().nullable(),
    credit_amount: yup
      .number()
      .typeError(<IntlMessages id='validation.creditAmount.type' />)
      .required(<IntlMessages id='validation.creditAmount.required' />)
      .test(
        'is-valid-amount',
       ()=> <IntlMessages id='validation.creditAmount.invalid' />,
        (value) =>
          /^-?\d+(\.\d{1,2})?$/.test(value) &&
          value >= -99999999.99 &&
          value <= 99999999.99,
      ),
    expiry_date: yup
      .date()
      .nullable()
      .typeError(<IntlMessages id='validation.startDate.type' />)
      .test({
        name: 'notFutureDate',
        exclusive: false,
        message: <IntlMessages id='validation.startDateNotFuture' />,
        test: function (value) {
          const today = new Date();
          const selectedDate = new Date(value);
          return (
            !value ||
            selectedDate.setHours(0, 0, 0, 0) >= today.setHours(0, 0, 0, 0)
          );
        },
      }),
  });
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [searchProduct, setSearchProduct] = useState('');
  const [search, setSearch] = useState('');
  const editorRef = useRef(null);
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.getContent();
    }
  }, []);
  const [productTypes, setProductTypes] = useState([]);
  const [selectedProductOption, setSelectedProductOption] = useState(null);
  const [parentMemberData, setParentMemberData] = useState([]);
  const [memberProductData, setMemberProductData] = useState([]);
  const [selectedUserOption, setSelectedUserOption] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  useEffect(() => {
    fetchProductTypes();
  }, [searchProduct]);

  const fetchProductTypes = async () => {
    try {
      const response = await jwtAxios.get(`/products`, {
        params: {
          search: searchProduct,
          perPage: 'all',
        },
      });
      const { data } = response;
      setProductTypes(data.list);
    } catch (error) {
      setProductTypes([]);
    }
  };

  useEffect(() => {
    fetchUsersData();
  }, [search]);

  const fetchUsersData = async () => {
    try {
      const response = await jwtAxios.get(`/users`, {
        params: {
          search: search,
          filters: {
            roleMenuFlag: 'student',
            is_active: 1,
          },
        },
      });
      const { data } = response;
      setUsers(data.list);
    } catch (error) {
      setUsers([]);
    }
  };
  useEffect(() => {
    fetchMembersData();
  }, []);

  const fetchMembersData = async () => {
    try {
      const response = await jwtAxios.get(`/user-members/${user.id}`, {
        params: {
          perPage: 5,
        },
      });
      const { data } = response;
      setParentMemberData(data.list);
    } catch (error) {
      setParentMemberData([]);
    }
  };
  useEffect(() => {
    if (selectedUserOption) {
      fetchUserMemberProduct();
    }
  }, [selectedUserOption]);

  const fetchUserMemberProduct = async () => {
    try {
      const response = await jwtAxios.get(
        `/products/productname/${selectedUserOption}`,
      );
      const { data } = response;
      setMemberProductData(data);
    } catch (error) {
      setMemberProductData([]);
    }
  };
  const handleClose = () => {
    setSelectedProductOption(null);
    setSearchProduct('');
    setSearch('');
  };

  const handleDialogOpen = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleCloseModalDialog = () => {
    setOpenDialog(false); // to close the dialog
    handleModalClose(); //to close the modal
  };
  const handleModalClose = () => {
    handleClose();
    onClose();
  };
  return (
    <>
      <Modal open={isOpen} onClose={handleDialogOpen}>
        <Box
          className='p-popup'
          sx={{
            bgcolor: 'background.paper',
            ...customScrollbarStyle,
          }}
        >
          <Formik
            initialValues={{
              user_id: [],
              product_id: '',
              description: '',
              credit_amount: '',
              expiry_date: null,
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(true);
              const userIdsToProcess =
                values.user_id && values.user_id.length > 0
                  ? values.user_id
                  : [user.id];

              for (const userId of userIdsToProcess) {
                const formData = {
                  ...values,
                  user_id: userId,
                };
                jwtAxios
                  .post(`/credits`, formData)
                  .then((response) => {
                    handleClose();
                    if (isAdjustCreditsModalOpen) {
                      fetchUserProduct();
                    } else {
                      fetchData();
                    }
                    setSubmitting(false);
                    handleCloseModalDialog();
                    infoViewActionsContext.showMessage(
                      'Credit has been added successfully!',
                    );
                  })
                  .catch((error) => {
                    infoViewActionsContext.fetchError(error.message);
                    setSubmitting(false);
                  });
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              setErrors,
              setFieldValue,
              handleBlur,
              setFieldTouched,
              isSubmitting,
            }) => (
              <Form noValidate autoComplete='off'>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography
                      variant='h4'
                      sx={{
                        color: companyTheme
                          ? companyTheme.primary.main
                          : 'text.primary',
                      }}
                    >
                      {isAdjustCreditsModalOpen
                        ? 'Adjust Credits'
                        : 'Add Credits'}{' '}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size='small'
                      sx={{ fontWeight: 'bold', color: '#000000' }}
                      className='icon-btn'
                      onClick={handleDialogOpen}
                    >
                      <CloseRounded />
                    </IconButton>
                  </Box>
                </Box>
                <AppGridContainer>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Box
                      sx={{
                        border: '1px solid #EAECF0',
                        p: 6,
                        borderRadius: 3,
                        boxShadow:
                          '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                      }}
                    >
                      <AppGridContainer>
                        {!isAdjustCreditsModalOpen && (
                          <>
                          <Grid size={{ xs: 12, md: 6 }}>
                          <Autocomplete
                                sx={{
                                  width: '100%',
                                }}
                                multiple
                                id='credit-member-id'
                                disableClearable
                                options={(users || [])
                                  .filter((user) => {
                                    return true;
                                  })
                                  .sort((a, b) =>
                                    (
                                      a.first_name +
                                      ' ' +
                                      a.last_name
                                    ).localeCompare(
                                      b.first_name + ' ' + b.last_name,
                                      undefined,
                                      { numeric: true, sensitivity: 'base' },
                                    ),
                                  )}
                                value={selectedUsers}
                                onChange={(event, newValue) => {
                                  const uniqueMembers = getUniqueMembers(newValue);
                                  const selectedUserIds = uniqueMembers.map((u) => u.id);
                                  setSelectedUsers(uniqueMembers);
                                  setFieldValue('user_id', selectedUserIds);
                                }}
                                onInputChange={(_, value) =>
                                  setSearch(value)
                                }
                                onBlur={() => setFieldTouched('user_id', true)}
                                getOptionLabel={(option) =>
                                  `${option.first_name} ${option.last_name} - ${option.email}`
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label='Member'
                                    name='user_id'
                                    value={values.user_id}
                                    error={touched.user_id && !values.user_id}
                                    InputProps={{// NOSONAR - MUI v6 Autocomplete still requires InputProps for dropdown anchor
                                     ...params.InputProps,
                                     type: 'search',
                                    }}
                                  />
                                )}
                              />
                              <FormHelperText
                                style={{ color: '#d32f2f', marginLeft: '15px' }}
                              >
                                {touched.user_id && errors.user_id
                                  ? 'Please choose a member'
                                  : ''}
                              </FormHelperText>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                disableClearable
                                sx={{
                                  width: '100%',
                                }}
                                options={(productTypes || []).sort((a, b) =>
                                  a.product_name.localeCompare(
                                    b.product_name,
                                    undefined,
                                    { numeric: true, sensitivity: 'base' },
                                  ),
                                )}
                                getOptionLabel={(option) =>
                                  option.badge
                                    ? `${option.product_name} - ${option.badge}`
                                    : option.product_name
                                }
                                value={
                                  productTypes.find(
                                    (product) =>
                                      product.id === selectedProductOption,
                                  ) || null
                                }
                                onChange={(event, newValue) => {
                                  const selectedProductId = newValue
                                    ? newValue.id
                                    : '';
                                  setSelectedProductOption(selectedProductId);
                                  if (setFieldValue) {
                                    setFieldValue(
                                      'product_id',
                                      selectedProductId,
                                    );
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label='Products'
                                    error={
                                      touched.product_id &&
                                      Boolean(errors.product_id)
                                    }
                                    helperText={
                                      touched.product_id && errors.product_id
                                        ? 'Please choose a product.'
                                        : ''
                                    }
                                  />
                                )}
                              />
                            </Grid>
                          </>
                        )}
                        {isAdjustCreditsModalOpen && (
                          <>
                            {isParent(user) && (
                              <Grid size={{ xs: 12, md: 6 }}>
                              <Autocomplete
                                  sx={{ width: '100%' }}
                                  options={(parentMemberData || []).sort(
                                    (a, b) =>
                                      a.first_name.localeCompare(
                                        b.first_name,
                                        undefined,
                                        {
                                          numeric: true,
                                          sensitivity: 'base',
                                        },
                                      ),
                                  )}
                                  getOptionLabel={(option) =>
                                    `${option.first_name} ${option.last_name} -${option.email}`
                                  }
                                  value={
                                    parentMemberData.find(
                                      (user) => user.id === selectedUserOption,
                                    ) || null
                                  }
                                  onChange={(event, newValue) => {
                                    const selectedUserId = newValue
                                      ? newValue.id
                                      : '';

                                    // Update local state
                                    setSelectedUserOption(selectedUserId);

                                    // Ensure `user_id` is set as an array
                                    if (setFieldValue) {
                                      setFieldValue(
                                        'user_id',
                                        selectedUserId ? [selectedUserId] : [],
                                      );
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label='Members'
                                      name='user_id'
                                      value={values.user_id}
                                      error={
                                        touched.user_id &&
                                        Boolean(errors.user_id)
                                      }
                                      helperText={
                                        touched.user_id && errors.user_id
                                          ? 'Please choose a user.'
                                          : ''
                                      }
                                      InputProps={{// NOSONAR - MUI v6 Autocomplete still requires InputProps for dropdown anchor
                                      ...params.InputProps,
                                    }}
                                    />
                                  )}
                                />
                              </Grid>
                            )}
                            <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                disabled={
                                  !selectedUserOption &&
                                  isParent(user)
                                }
                                sx={{
                                  width: '100%',
                                }}
                                options={(selectedUserOption
                                  ? memberProductData
                                  : settings || []
                                ).sort((a, b) =>
                                  a.product_name.localeCompare(
                                    b.product_name,
                                    undefined,
                                    {
                                      numeric: true,
                                      sensitivity: 'base',
                                    },
                                  ),
                                )}
                                getOptionLabel={(option) =>
                                  `${option.product_name} - ${
                                    option.badge !== null ? option.badge : '-'
                                  }`
                                }
                                value={
                                  (selectedUserOption
                                    ? memberProductData
                                    : settings
                                  ).find(
                                    (product) =>
                                      product.product_id ===
                                      selectedProductOption,
                                  ) || null
                                }
                                onChange={(event, newValue) => {
                                  const selectedProductId = newValue
                                    ? newValue.product_id
                                    : '';
                                  setSelectedProductOption(selectedProductId);
                                  if (setFieldValue) {
                                    setFieldValue(
                                      'product_id',
                                      selectedProductId,
                                    );
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label='Products'
                                    error={
                                      touched.product_id &&
                                      Boolean(errors.product_id)
                                    }
                                    helperText={
                                      touched.product_id && errors.product_id
                                        ? 'Please choose a product.'
                                        : ''
                                    }
                                  />
                                )}
                              />
                            </Grid>
                          </>
                        )}
                        <Grid size={{ xs: 12, md: 12 }}>
                          <AppTextField
                            name='description'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Description'
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                            name='credit_amount'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Credit Amount'
                          />
                        </Grid>

                        <Grid size={{ xs: 12, md: 12 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                backgroundColor: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                                '&:hover': {
                                  backgroundColor: companyTheme
                                    ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                                    : alpha('#2196f3', 0.8),
                                },
                              }}
                              variant='contained'
                              type='submit'
                            >
                              <IntlMessages id='common.save' />
                            </Button>
                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                ml: 2.5,
                                borderColor: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                                '&:hover': {
                                  borderColor: companyTheme
                                    ? alpha(companyTheme.primary.main, 0.8)
                                    : alpha('#2196f3', 0.8),
                                },
                                color: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                              }}
                              variant='outlined'
                              onClick={handleDialogOpen}
                            >
                              <IntlMessages id='common.cancel' />
                            </Button>
                          </Box>
                        </Grid>
                      </AppGridContainer>
                    </Box>
                  </Grid>
                </AppGridContainer>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
      <AppModalConfirmation
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        handleCloseModalDialog={handleCloseModalDialog}
      />
    </>
  );
};

export default AddCredits;
AddCredits.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fetchData: PropTypes.func,
  isAdjustCreditsModalOpen: PropTypes.bool,
  settings: PropTypes.object,
  fetchUserProduct: PropTypes.func,
};
